import { useEffect } from 'react';
import { Link } from 'react-router-dom';

// Static legal page. Reachable at /politique-de-confidentialite and linked from
// the footer. Content is RGPD-oriented for an appointment-based practice; the
// practitioner should confirm the specifics (legal entity, retention, hosting
// region) before relying on it.
export default function Privacy() {
  useEffect(() => {
    document.title = 'Politique de confidentialité — LIFECUPP by Hijama';
    window.scrollTo(0, 0);
  }, []);

  const updated = '21 juillet 2026';

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-emerald-950">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <Link to="/" className="text-emerald-400 hover:text-emerald-300 text-sm transition-colors">
            ← Retour au site
          </Link>
          <h1
            className="text-3xl md:text-4xl font-bold text-white mt-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Politique de confidentialité
          </h1>
          <p className="text-emerald-200/70 text-sm mt-2">Dernière mise à jour : {updated}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="prose-sm space-y-8 text-stone-700 leading-relaxed">
          <section>
            <p>
              Chez <strong>LIFECUPP by Hijama</strong>, la protection de vos données personnelles
              est une priorité. Cette politique explique quelles informations nous recueillons,
              pourquoi, comment elles sont protégées, et quels sont vos droits. Nous ne vendons ni
              ne partageons jamais vos données à des fins commerciales ou publicitaires.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-stone-800 mb-3">1. Données que nous collectons</h2>
            <p>
              Lorsque vous prenez rendez-vous via notre formulaire en ligne, nous recueillons
              uniquement les informations nécessaires à la gestion de votre demande :
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>votre prénom et votre nom ;</li>
              <li>votre adresse e-mail et votre numéro de téléphone ;</li>
              <li>la prestation souhaitée, la date et l'heure demandées ;</li>
              <li>
                le message facultatif que vous choisissez de nous transmettre (par exemple les
                zones concernées ou vos éventuelles douleurs).
              </li>
            </ul>
            <p className="mt-3">
              Ce message peut contenir des informations relatives à votre santé. Ces données, dites
              « sensibles », ne sont traitées qu'avec votre accord, dans le seul but de préparer et
              d'assurer votre séance, et bénéficient d'une protection renforcée.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-stone-800 mb-3">2. Pourquoi nous les utilisons</h2>
            <p>Vos données servent exclusivement à :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>traiter votre demande de rendez-vous et vous recontacter ;</li>
              <li>vous envoyer la confirmation de votre rendez-vous ;</li>
              <li>assurer le suivi de votre séance.</li>
            </ul>
            <p className="mt-3">
              La base légale de ce traitement est votre consentement et l'exécution des mesures
              prises à votre demande avant la prestation. Nous n'utilisons jamais vos données pour
              de la prospection non sollicitée.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-stone-800 mb-3">3. Qui y a accès</h2>
            <p>
              Seul le praticien de LIFECUPP by Hijama a accès à vos informations. Elles ne sont
              <strong> ni vendues, ni louées, ni transmises</strong> à des tiers à des fins
              commerciales.
            </p>
            <p className="mt-3">
              Pour fonctionner, le site s'appuie sur des prestataires techniques (hébergement
              sécurisé des données et envoi des e-mails de confirmation). Ces prestataires agissent
              en tant que sous-traitants, uniquement sur nos instructions, et n'utilisent jamais vos
              données pour leur propre compte.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-stone-800 mb-3">4. Protection et conservation</h2>
            <p>
              Vos données sont stockées sur des serveurs sécurisés, protégées par des mesures
              techniques strictes (accès restreint, chiffrement des échanges, authentification).
              Elles ne sont accessibles à personne d'autre que le praticien.
            </p>
            <p className="mt-3">
              Nous conservons vos informations uniquement le temps nécessaire au suivi de votre
              relation avec le cabinet, puis elles sont supprimées ou anonymisées.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-stone-800 mb-3">5. Vos droits</h2>
            <p>
              Conformément au Règlement général sur la protection des données (RGPD), vous disposez
              à tout moment des droits suivants sur vos données :
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>droit d'accès : savoir quelles données nous détenons sur vous ;</li>
              <li>droit de rectification : corriger des informations inexactes ;</li>
              <li>droit à l'effacement : demander la suppression de vos données ;</li>
              <li>droit d'opposition et de limitation du traitement ;</li>
              <li>droit à la portabilité de vos données.</li>
            </ul>
            <p className="mt-3">
              Pour exercer ces droits, il vous suffit de nous contacter (voir ci-dessous). Vous
              pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-stone-800 mb-3">6. Cookies</h2>
            <p>
              Ce site n'utilise pas de cookies publicitaires ni de traceurs de suivi. Seules des
              données techniques strictement nécessaires au bon fonctionnement du site peuvent être
              stockées dans votre navigateur (par exemple votre choix de langue).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-stone-800 mb-3">7. Contact</h2>
            <p>
              Pour toute question relative à vos données personnelles ou pour exercer vos droits,
              contactez-nous :
            </p>
            <ul className="list-none mt-2 space-y-1">
              <li>
                E-mail :{' '}
                <a href="mailto:contact@lifecupp.com" className="text-emerald-700 hover:underline">
                  contact@lifecupp.com
                </a>
              </li>
              <li>Cabinet LIFECUPP by Hijama — 5 Rue du Clos Fleury, 74100 Annemasse</li>
            </ul>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-stone-200">
          <Link to="/" className="text-emerald-700 hover:text-emerald-900 text-sm transition-colors">
            ← Retour à l'accueil
          </Link>
        </div>
      </main>
    </div>
  );
}
